import { collection, setDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { activityToFirestore } from "../models/activity";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";
import { DEFAULT_ACTIVITIES } from "../constants/defaultActivities";

/**
 * ActivityService
 * Centraliza as operações de banco de dados para o módulo de Atividades.
 */
const ActivityService = {
    /**
     * Busca todas as atividades de um tenant.
     */
    getActivities: async (idTenant) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES),
                orderBy("order", "asc")
            );
            const querySnapshot = await getDocs(q);
            const activities = [];
            querySnapshot.forEach((doc) => {
                activities.push({ id: doc.id, ...doc.data() });
            });
            return activities;
        } catch (error) {
            console.error("Erro ao listar atividades:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza uma atividade.
     */
    save: async (formData, idTenant, authUser, existingActivities = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const activityData = activityToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const activityRef = doc(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES, formData.id);
                const oldActivity = existingActivities.find(a => a.id === formData.id) || {};
                await updateDoc(activityRef, activityData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldActivity, activityData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.ACTIVITIES,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: activityData.name,
                    description: `Atualizou a atividade "${activityData.name}"`,
                    details: changes
                });

                return { id: formData.id, ...activityData };
            } else {
                // CREATE
                const customId = Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
                const newDocRef = doc(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES, customId);
                await setDoc(newDocRef, activityData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.ACTIVITIES,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: activityData.name,
                    description: `Criou a atividade "${activityData.name}"`
                });

                return { id: newDocRef.id, ...activityData };
            }
        } catch (error) {
            console.error("Erro no ActivityService.save:", error);
            throw error;
        }
    },

    /**
     * Remove uma atividade.
     */
    delete: async (activityId, idTenant, authUser, activityName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES, activityId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.ACTIVITIES,
                action: AUDIT_ACTIONS.DELETE,
                entityId: activityId,
                entityName: activityName,
                description: `Excluiu a atividade "${activityName || activityId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no ActivityService.delete:", error);
            throw error;
        }
    },

    /**
     * Reordena a lista de atividades em massa.
     */
    reorder: async (items, idTenant, authUser) => {
        if (!idTenant || !items.length) return;

        try {
            const batch = writeBatch(db);
            items.forEach((item, index) => {
                const docRef = doc(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES, item.id);
                batch.update(docRef, { order: index });
            });

            await batch.commit();

            // Auditoria simplificada do lote
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.ACTIVITIES,
                action: AUDIT_ACTIONS.UPDATE,
                description: `Reordenou a lista de atividades (${items.length} itens)`
            });

            return true;
        } catch (error) {
            console.error("Erro no ActivityService.reorder:", error);
            throw error;
        }
    },

    /**
     * Cria as atividades padrão para um tenant que ainda não tem nenhuma.
     * Deve ser chamada na primeira configuração do tenant.
     * 
     * @param {string} idTenant - ID do tenant
     * @param {Object} authUser - Usuário que está fazendo o seed
     * @returns {number} Quantidade de atividades criadas
     */
    seedDefaults: async (idTenant, authUser) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            // Verifica se já existem atividades
            const existing = await ActivityService.getActivities(idTenant);
            if (existing.length > 0) {
                console.info("[ActivityService] Tenant já possui atividades. Seed ignorado.");
                return 0;
            }

            // Cria todas as atividades padrão
            let count = 0;
            for (const activity of DEFAULT_ACTIVITIES) {
                const activityData = activityToFirestore(activity, { id: idTenant }, authUser);
                const customId = Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
                await setDoc(
                    doc(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES, customId),
                    activityData
                );
                count++;
            }

            console.info(`[ActivityService] Seed concluído: ${count} atividades criadas.`);
            return count;
        } catch (error) {
            console.error("Erro no ActivityService.seedDefaults:", error);
            throw error;
        }
    }
};

export default ActivityService;
